import { useState, useEffect, useRef } from "react";
import { 
  Plus, Edit2, Trash2, Loader2, Search, X, Star, BookOpen, 
  ChevronLeft, ChevronRight, Check, FileText, Zap, GripVertical,
  ArrowUpDown, ArrowUp, ArrowDown, Users, Blocks, Code2, Wand2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { MODES, MODE_META, modeLabel, normalizeMode } from "../../lib/modes";
import { AutoBuildDrillsDialog } from "./AutoBuildDrillsDialog";

const API = '/api';

// Difficulty star display
function DifficultyStars({ difficulty, size = "sm" }) {
  const starSize = size === "sm" ? "w-4 h-4" : "w-5 h-5";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${starSize} ${star <= difficulty ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );
}

// Clickable difficulty selector
function DifficultySelector({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none hover:scale-110 transition-transform"
        >
          <Star
            className={`w-6 h-6 ${star <= value ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-200"}`}
          />
        </button>
      ))}
      <span className="ml-2 text-sm text-muted-foreground">({value}/5)</span>
    </div>
  );
}

// Draggable question list for reordering
function DraggableQuestionList({ questionIds, questionsData, onReorder, onRemove }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedIndex;
    
    if (dragIndex !== null && dragIndex !== dropIndex) {
      const newOrder = [...questionIds];
      const [removed] = newOrder.splice(dragIndex, 1);
      newOrder.splice(dropIndex, 0, removed);
      onReorder(newOrder);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Get question details by ID
  const getQuestionDetails = (id) => {
    return questionsData.find(q => q.question_id === id) || { title: id, pool: "unknown" };
  };

  if (questionIds.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        No questions selected. Click "Add Questions" to select questions for this drill.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {questionIds.map((id, index) => {
        const question = getQuestionDetails(id);
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;
        
        return (
          <div
            key={id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`
              flex items-center gap-2 p-2 rounded-lg border bg-background
              transition-all duration-150 cursor-grab active:cursor-grabbing
              ${isDragging ? "opacity-50 scale-[0.98] border-primary" : ""}
              ${isDragOver ? "border-primary border-dashed bg-primary/5" : "border-border"}
              hover:border-primary/50
            `}
            data-testid={`draggable-question-${index}`}
          >
            <div className="flex items-center justify-center w-6 h-6 text-muted-foreground hover:text-foreground">
              <GripVertical className="w-4 h-4" />
            </div>
            <span className="text-xs font-medium text-muted-foreground w-6">
              {index + 1}.
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{question.title}</p>
            </div>
            <Badge 
              variant="outline" 
              className={`shrink-0 text-xs ${
                question.pool === "daily" 
                  ? "text-primary border-primary" 
                  : "text-secondary border-secondary"
              }`}
            >
              {question.pool === "daily" ? "Warm Up" : "Challenge"}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
              data-testid={`remove-question-${index}`}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

// Question selector for adding to drill
function QuestionSelector({ selectedIds, onSelectionChange, onClose, mode = "python" }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [poolFilter, setPoolFilter] = useState("all");
  const [localSelection, setLocalSelection] = useState(new Set(selectedIds));

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${API}/admin/drills/questions/search?limit=2000&mode=${mode}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredQuestions = questions.filter(q => {
    const matchesSearch = !searchTerm || 
      q.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPool = poolFilter === "all" || q.pool === poolFilter;
    return matchesSearch && matchesPool;
  });

  const toggleQuestion = (questionId) => {
    const newSelection = new Set(localSelection);
    if (newSelection.has(questionId)) {
      newSelection.delete(questionId);
    } else {
      newSelection.add(questionId);
    }
    setLocalSelection(newSelection);
  };

  const handleSave = () => {
    onSelectionChange(Array.from(localSelection));
    onClose();
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="drill-question-search"
          />
        </div>
        <Select value={poolFilter} onValueChange={setPoolFilter}>
          <SelectTrigger className="w-[140px]" data-testid="drill-pool-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pools</SelectItem>
            <SelectItem value="daily">Warm Up</SelectItem>
            <SelectItem value="challenge">Challenge</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-muted-foreground">
        {localSelection.size} questions selected
      </div>

      <div className="border rounded-lg max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="w-32">Difficulty</TableHead>
                <TableHead className="w-24">Pool</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuestions.map((q) => (
                <TableRow 
                  key={q.question_id}
                  className={`cursor-pointer ${localSelection.has(q.question_id) ? "bg-primary/10" : "hover:bg-muted/50"}`}
                  onClick={() => toggleQuestion(q.question_id)}
                >
                  <TableCell>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      localSelection.has(q.question_id) 
                        ? "bg-primary border-primary" 
                        : "border-gray-300"
                    }`}>
                      {localSelection.has(q.question_id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{q.title}</TableCell>
                  <TableCell data-testid={`picker-difficulty-${q.question_id}`}>
                    {q.difficulty ? <DifficultyStars difficulty={q.difficulty} /> : <span className="text-xs text-muted-foreground">Unrated</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={q.pool === "daily" ? "text-primary border-primary" : "text-secondary border-secondary"}>
                      {q.pool === "daily" ? <FileText className="w-3 h-3 mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                      {q.pool === "daily" ? "Warm Up" : "Challenge"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {filteredQuestions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No questions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} data-testid="save-question-selection-btn">
          Save Selection ({localSelection.size})
        </Button>
      </div>
    </div>
  );
}

export function DrillsTab({ isMasterAdmin = false }) {
  const [drills, setDrills] = useState([]);
  const [autoBuildOpen, setAutoBuildOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isQuestionSelectorOpen, setIsQuestionSelectorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingDrill, setDeletingDrill] = useState(null);
  const [editingDrill, setEditingDrill] = useState(null);
  const [questionsData, setQuestionsData] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    difficulty: 3,
    question_ids: [],
    mode: "python"
  });
  const [track, setTrackState] = useState(() => normalizeMode(localStorage.getItem("admin_drills_track")));
  const setTrack = (t) => { localStorage.setItem("admin_drills_track", t); setTrackState(t); };
  const drillsInTrack = drills.filter((d) => normalizeMode(d.mode) === track);
  const trackCounts = Object.fromEntries(MODES.map((m) => [m, drills.filter((d) => normalizeMode(d.mode) === m).length]));
  
  useEffect(() => {
    fetchDrills();
    fetchQuestionsData();
  }, []);

  // Re-fetch when sort changes
  useEffect(() => {
    if (!loading) {
      fetchDrills();
    }
  }, [sortBy, sortOrder]);

  const fetchDrills = async () => {
    try {
      const response = await fetch(`${API}/admin/drills?sort_by=${sortBy}&sort_order=${sortOrder}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setDrills(data.drills || []);
      } else {
        toast.error("Failed to load drills");
      }
    } catch (error) {
      console.error("Error fetching drills:", error);
      toast.error("Error loading drills");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // New field, default to desc for most, asc for title
      setSortBy(field);
      setSortOrder(field === "title" ? "asc" : "desc");
    }
  };

  const fetchQuestionsData = async () => {
    try {
      const perMode = await Promise.all(MODES.map((m) =>
        fetch(`${API}/admin/drills/questions/search?limit=2000&mode=${m}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : { questions: [] }))
      ));
      setQuestionsData(perMode.flatMap((d) => d.questions || []));
    } catch (error) {
      console.error("Error fetching questions data:", error);
    }
  };

  const openNewDrill = () => {
    setEditingDrill(null);
    setFormData({
      title: "",
      description: "",
      difficulty: 3,
      question_ids: [],
      mode: track
    });
    setIsDialogOpen(true);
  };

  const openEditDrill = async (drill) => {
    // Fetch full drill details including questions
    try {
      const response = await fetch(`${API}/admin/drills/${drill.drill_id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        const fullDrill = data.drill;
        setEditingDrill(fullDrill);
        setFormData({
          title: fullDrill.title || "",
          description: fullDrill.description || "",
          difficulty: fullDrill.difficulty || 3,
          question_ids: fullDrill.question_ids || [],
          mode: normalizeMode(fullDrill.mode)
        });
        setIsDialogOpen(true);
      } else {
        toast.error("Failed to load drill details");
      }
    } catch (error) {
      toast.error("Error loading drill");
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    setSaving(true);
    try {
      const isNew = !editingDrill;
      const url = isNew 
        ? `${API}/admin/drills` 
        : `${API}/admin/drills/${editingDrill.drill_id}`;
      
      const response = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isNew ? "Drill created!" : "Drill updated!");
        setIsDialogOpen(false);
        fetchDrills();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save drill");
      }
    } catch (error) {
      toast.error("Error saving drill");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (drill) => {
    setDeletingDrill(drill);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingDrill) return;

    try {
      const response = await fetch(`${API}/admin/drills/${deletingDrill.drill_id}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Drill deleted!");
        setIsDeleteDialogOpen(false);
        setDeletingDrill(null);
        fetchDrills();
      } else {
        toast.error("Failed to delete drill");
      }
    } catch (error) {
      toast.error("Error deleting drill");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl border border-border/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/10">
              <BookOpen className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg">Drills</h2>
              <p className="text-sm text-muted-foreground">
                {drillsInTrack.length} {modeLabel(track)} drill{drillsInTrack.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center rounded-xl border border-border bg-muted/40 p-1" role="tablist" data-testid="drills-track-tabs">
              {MODES.map((v) => ({ v, label: MODE_META[v].label, Icon: MODE_META[v].icon })).map(({ v, label, Icon }) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={track === v}
                  onClick={() => setTrack(v)}
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${track === v ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`drills-track-${v}`}
                >
                  <Icon className="w-4 h-4" /> {label} drills
                  <Badge variant="secondary" className="px-1.5 py-0 text-xs">{trackCounts[v]}</Badge>
                </button>
              ))}
            </div>
            {isMasterAdmin && (
              <Button variant="outline" onClick={() => setAutoBuildOpen(true)} className="gap-2 rounded-xl" data-testid="auto-build-drills-btn" title="Build drills from questions not yet in a drill, grouped by similar difficulty">
                <Wand2 className="w-4 h-4 text-purple-500" /> Auto-build
              </Button>
            )}
            <Button
              onClick={openNewDrill}
              className="gap-2 rounded-xl"
              data-testid="add-drill-btn"
            >
              <Plus className="w-4 h-4" />
              Create {track === "python" ? "" : `${modeLabel(track)} `}Drill
            </Button>
          </div>
        </div>
        {isMasterAdmin && autoBuildOpen && (
          <AutoBuildDrillsDialog open={autoBuildOpen} onOpenChange={setAutoBuildOpen} defaultMode={track} onCreated={(data) => { fetchDrills(); if (data.mode && data.mode !== track) setTrack(data.mode); }} />
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    onClick={() => handleSort("title")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                    data-testid="sort-title-btn"
                  >
                    Title
                    {sortBy === "title" ? (
                      sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead>Description</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("difficulty")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                    data-testid="sort-difficulty-btn"
                  >
                    Difficulty
                    {sortBy === "difficulty" ? (
                      sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("question_count")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                    data-testid="sort-questions-btn"
                  >
                    Questions
                    {sortBy === "question_count" ? (
                      sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drillsInTrack.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No {modeLabel(track)} drills yet - click "Create {track === "python" ? "" : `${modeLabel(track)} `}Drill".
                  </TableCell>
                </TableRow>
              )}
              {drillsInTrack.map((drill) => {
                return (
                <TableRow key={drill.drill_id}>
                  <TableCell className="font-medium">{drill.title}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[200px] truncate">
                    {drill.description || "—"}
                  </TableCell>
                  <TableCell>
                    <DifficultyStars difficulty={drill.difficulty} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {drill.question_count || 0} questions
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDrill(drill)}
                      data-testid={`edit-drill-${drill.drill_id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => confirmDelete(drill)}
                      data-testid={`delete-drill-${drill.drill_id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="drill-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingDrill ? "Edit Drill" : "Create New Drill"}
            </DialogTitle>
            <DialogDescription>
              Group questions together to create a structured practice drill.
            </DialogDescription>
          </DialogHeader>

          {isQuestionSelectorOpen ? (
            <QuestionSelector
              selectedIds={formData.question_ids}
              onSelectionChange={(ids) => setFormData(prev => ({ ...prev, question_ids: ids }))}
              onClose={() => setIsQuestionSelectorOpen(false)}
              mode={formData.mode}
            />
          ) : (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid="drill-track-section">
                <div>
                  <label className="text-sm font-medium block">Track</label>
                  <p className="text-xs text-muted-foreground">{formData.mode === "junior" ? "Every question is a drill navigation grid solved with blocks." : formData.mode === "blocks" ? "Students solve every question with blocks." : "Students type Python."}{formData.question_ids.length > 0 ? " Remove the questions to change track." : ""}</p>
                </div>
                <div className="inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5" role="radiogroup" data-testid="drill-mode-toggle">
                  {MODES.map((v) => ({ v, label: MODE_META[v].label, Icon: MODE_META[v].icon })).map(({ v, label, Icon }) => (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={formData.mode === v}
                      disabled={formData.question_ids.length > 0 && formData.mode !== v}
                      onClick={() => setFormData((prev) => ({ ...prev, mode: v }))}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition-colors disabled:opacity-40 ${formData.mode === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`drill-mode-${v}`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Python Basics - Week 1"
                  data-testid="drill-title-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of what this drill covers..."
                  className="min-h-[80px]"
                  data-testid="drill-description-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Difficulty</label>
                <DifficultySelector
                  value={formData.difficulty}
                  onChange={(val) => setFormData(prev => ({ ...prev, difficulty: val }))}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">
                    Questions {formData.question_ids.length > 0 && (
                      <span className="text-muted-foreground font-normal">
                        ({formData.question_ids.length} selected)
                      </span>
                    )}
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsQuestionSelectorOpen(true)}
                    className="gap-1"
                    data-testid="select-questions-btn"
                  >
                    <Plus className="w-4 h-4" />
                    {formData.question_ids.length === 0 ? "Add Questions" : "Add More"}
                  </Button>
                </div>
                <div className="border rounded-lg p-3 bg-muted/30 max-h-[200px] overflow-y-auto">
                  <DraggableQuestionList
                    questionIds={formData.question_ids}
                    questionsData={questionsData}
                    onReorder={(newOrder) => setFormData(prev => ({ ...prev, question_ids: newOrder }))}
                    onRemove={(idToRemove) => setFormData(prev => ({
                      ...prev,
                      question_ids: prev.question_ids.filter(id => id !== idToRemove)
                    }))}
                  />
                </div>
                {formData.question_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                    <GripVertical className="w-3 h-3" />
                    Drag to reorder questions
                  </p>
                )}
              </div>
            </div>
          )}

          {!isQuestionSelectorOpen && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-drill-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  editingDrill ? "Update Drill" : "Create Drill"
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Drill</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingDrill?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-drill-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
